/**
 * Pure rules for the check an instrument added to a quantity composition passes before it is
 * priced: which issues the user can fix, what the engine's answer does to the row, and what the
 * rows' check states mean for the save.
 *
 * | status      | row                                   | priced         | save                   |
 * |-------------|---------------------------------------|----------------|------------------------|
 * | `checking`  | locked                                | no             | blocked                |
 * | `clear`     | editable                              | from the answer| ok                     |
 * | `fixable`   | locked, issue opens its fix           | once fixed     | blocked                |
 * | `unfixable` | locked, greyed                        | never          | ok, row left out       |
 * | `failed`    | locked, issue retries the check       | no             | blocked                |
 *
 * A locked row holds no shares, no market value and no weight, so cash is untouched (I1) for as
 * long as it stays locked. Nothing here calls the API or touches the builder.
 */
import {
	type IndexTicker,
	type InstrumentPricing,
	InstrumentType,
	type NewInstrument,
	type ResolvedInstrument,
	type ResolvedInstrumentIssueEnum,
	type RichRiskModelValue,
} from "@eu.mdotm.api/sphere";
import type { Instrument, NewInstrumentCheck } from "../../const";
import type { PricingByIdentifier } from "../market-date/pricing";
import { pricingIdentifier } from "../market-date/pricing";

type CheckStatus = NewInstrumentCheck["status"];

/** Issues the user can act on from the row. Every other issue means the instrument cannot be held. */
const FIXABLE_ISSUES: ReadonlySet<ResolvedInstrumentIssueEnum> = new Set<ResolvedInstrumentIssueEnum>([
	"UNRECOGNIZED_INSTRUMENT",
	"MISSING_RISK_MODEL",
	"RISK_MODEL_SUGGESTION_REQUIRES_REVIEW",
	"NOT_SUPPORTED_BOND",
	"MISSING_CLOSES",
]);

/** The status an issue puts a row in once the engine has answered. */
export function classifyIssue(
	issue: ResolvedInstrumentIssueEnum | undefined,
): Extract<CheckStatus, "clear" | "fixable" | "unfixable"> {
	if (issue == null) {
		return "clear";
	}
	return FIXABLE_ISSUES.has(issue) ? "fixable" : "unfixable";
}

/**
 * How a row names itself to the check: the identifier the user typed or the alias of the instrument
 * picked from the search, plus the platform ticker when the picker supplied one.
 */
export function checkRequest(row: Instrument): NewInstrument {
	const identifier = (row.alias ?? row.tickerName ?? row.id).trim();
	return row.tickerName ? { identifier, tickerName: row.tickerName } : { identifier };
}

/** Whether a check may run on the row: nested portfolios and cash are never checked. */
export function isCheckable(row: Instrument): boolean {
	return row.instrumentType === "INSTRUMENT" && !row.isCash;
}

/** What the user did about a row's issue, as the builder applies it. */
export type NewInstrumentFix =
	| { kind: "identifier"; identifier: string }
	| { kind: "riskModel"; proxies: IndexTicker[] }
	| { kind: "retry" };

/** What one call asks about `rows`: each distinct identifier once, in row order. */
export function checkRequests(rows: Iterable<Instrument>): NewInstrument[] {
	const requests = new Map<string, NewInstrument>();
	for (const row of rows) {
		const request = row.newInstrumentCheck?.request;
		if (request?.identifier && !requests.has(request.identifier)) {
			requests.set(request.identifier, request);
		}
	}
	return Array.from(requests.values());
}

/** The row with a check going out for it — asked with `request` at `date`. */
export function startCheck(
	row: Instrument,
	params: { request: NewInstrument; date: string; attempt: number },
): Instrument {
	return {
		...row,
		newInstrumentCheck: {
			status: "checking",
			request: params.request,
			checkedAt: params.date,
			attempt: params.attempt,
		},
	};
}

/** The row checked again, with what it was checked with last time. */
export function restartCheck(row: Instrument, params: { date: string; attempt: number }): Instrument {
	const check = row.newInstrumentCheck;
	if (!check) {
		return row;
	}
	return startCheck(row, { request: check.request, ...params });
}

/**
 * The fix for an unrecognised instrument: the identifier the user gave replaces the old one, and
 * the row is checked again from scratch — the platform ticker it had named the instrument that was
 * not recognised, so it goes with it.
 */
export function withNewIdentifier(
	row: Instrument,
	params: { identifier: string; date: string; attempt: number },
): Instrument {
	const identifier = params.identifier.trim();
	return startCheck(
		{ ...row, alias: identifier, tickerName: undefined, riskModel: undefined },
		{ request: { identifier }, date: params.date, attempt: params.attempt },
	);
}

/**
 * The fix for every other fixable issue: the risk model the user chose. The row is clear from here
 * and is priced like any other.
 */
export function withRiskModel(row: Instrument, proxies: IndexTicker[]): Instrument {
	const check = row.newInstrumentCheck;
	if (!check) {
		return row;
	}
	return {
		...row,
		riskModel: proxies.map(riskModelFromProxy),
		newInstrumentCheck: { ...check, status: "clear", issueResolved: true },
	};
}

/** Whether the row is waiting on the check numbered `attempt`. */
function isAwaiting(row: Instrument, attempt: number): boolean {
	return row.newInstrumentCheck?.status === "checking" && row.newInstrumentCheck.attempt === attempt;
}

/**
 * Applies the engine's answer to the check numbered `attempt`: each row still waiting on it takes
 * what the engine said about its identifier, and each one that came back clear gets a pricing
 * answer to go through the same application every price does (`../market-date/pricing`).
 *
 * `answers` is `undefined` when the call failed; a row the engine said nothing about fails too.
 * Rows waiting on another attempt — checked again since — are passed through untouched, so an
 * answer that lands late cannot overwrite a newer one.
 */
export function applyCheckAnswers(
	rows: Instrument[],
	answers: ResolvedInstrument[] | undefined,
	attempt: number,
): { rows: Instrument[]; pricing: PricingByIdentifier } {
	const byIdentifier = new Map<string, ResolvedInstrument>();
	for (const answer of answers ?? []) {
		if (answer.identifier) {
			byIdentifier.set(answer.identifier, answer);
		}
	}

	const pricing: PricingByIdentifier = new Map();
	const checked = rows.map((row) => {
		const check = row.newInstrumentCheck;
		if (!check || !isAwaiting(row, attempt)) {
			return row;
		}
		const answer = check.request.identifier ? byIdentifier.get(check.request.identifier) : null;
		if (!answer) {
			return { ...row, newInstrumentCheck: { ...check, status: "failed" as const } };
		}
		const resolved = withResolution(row, check, answer);
		const identifier = pricingIdentifier(resolved);
		if (resolved.newInstrumentCheck?.status === "clear" && identifier) {
			pricing.set(identifier, pricingFromResolution(identifier, answer));
		}
		return resolved;
	});
	return { rows: checked, pricing };
}

const INSTRUMENT_TYPES: ReadonlySet<string> = new Set<string>(Object.values(InstrumentType));

/** Whether the engine's type is one a row can carry. */
function isInstrumentType(type: string | undefined): type is InstrumentType {
	return type != null && INSTRUMENT_TYPES.has(type);
}

/** The row as the engine resolved it, its type included. */
function withResolution(row: Instrument, check: NewInstrumentCheck, answer: ResolvedInstrument): Instrument {
	return {
		...row,
		tickerName: answer.ticker ?? row.tickerName,
		name: answer.name ?? row.name,
		type: isInstrumentType(answer.type) ? answer.type : row.type,
		riskModel: answer.proxies ? answer.proxies.map(riskModelFromProxy) : row.riskModel,
		proxyOverwriteType: answer.proxyOverwriteType ?? row.proxyOverwriteType,
		newInstrumentCheck: {
			...check,
			status: classifyIssue(answer.issue),
			issue: answer.issue,
			needsCustomProxy: answer.needsCustomProxy,
		},
	};
}

function pricingFromResolution(identifier: string, answer: ResolvedInstrument): InstrumentPricing {
	return {
		identifier,
		holdingMode: answer.holdingMode,
		price: answer.price,
		priceDate: answer.priceDate,
		unpricedReason: answer.unpricedReason,
	};
}

/** A risk-model proxy as the row carries it. */
export function riskModelFromProxy(proxy: IndexTicker): RichRiskModelValue {
	return {
		ticker: proxy.ticker,
		weight: proxy.weight,
		name: proxy.instrument,
		granularity: proxy.granularity,
		macroAssetClass: proxy.assetClass,
		macroGeography: proxy.geography,
		microAssetClass: proxy.microAssetClass,
		microGeography: proxy.microGeography,
	};
}

/** The row's risk model as the classification dialog takes it. */
export function proxiesFromRiskModel(row: Instrument): IndexTicker[] {
	return (row.riskModel ?? []).map((value) => ({
		ticker: value.ticker,
		weight: value.weight,
		instrument: value.name,
		granularity: value.granularity,
		assetClass: value.macroAssetClass,
		geography: value.macroGeography,
		microAssetClass: value.microAssetClass,
		microGeography: value.microGeography,
	}));
}

function statusOf(row: Instrument): CheckStatus | undefined {
	return row.newInstrumentCheck?.status;
}

/** A row the check has locked. */
export type LockedInstrument = Instrument & {
	newInstrumentCheck: NewInstrumentCheck & { status: Exclude<CheckStatus, "clear"> };
};

/** Whether the check has the row locked: nothing about it can be edited, and it counts for nothing. */
export function isLocked(row: Instrument): row is LockedInstrument {
	const status = statusOf(row);
	return status != null && status !== "clear";
}

/** The rows whose check must run again at `date`: every one still owed an answer or a fix there. */
export function rowsToRecheck(rows: Iterable<Instrument>, date: string): Instrument[] {
	return Array.from(rows).filter((row) => {
		const status = statusOf(row);
		return (
			(status === "checking" || status === "fixable" || status === "failed") &&
			row.newInstrumentCheck?.checkedAt !== date
		);
	});
}

/**
 * How many rows are still being checked, how many the check could not answer about, how many wait
 * on the user's fix, and how many cannot be held. Soft-deleted rows count for none of them: they
 * leave the composition at save.
 */
export function checkCounts(rows: Iterable<Instrument>): {
	checking: number;
	failed: number;
	needFix: number;
	cannotHold: number;
} {
	const counts = { checking: 0, failed: 0, needFix: 0, cannotHold: 0 };
	for (const row of rows) {
		if (row.deleted) {
			continue;
		}
		const status = statusOf(row);
		if (status === "checking") {
			counts.checking += 1;
		} else if (status === "failed") {
			counts.failed += 1;
		} else if (status === "fixable") {
			counts.needFix += 1;
		} else if (status === "unfixable") {
			counts.cannotHold += 1;
		}
	}
	return counts;
}

/** True while a row is being checked, was not answered about, or waits on a fix — submit must be blocked. */
export function blocksSave(rows: Iterable<Instrument>): boolean {
	const { checking, failed, needFix } = checkCounts(rows);
	return checking + failed + needFix > 0;
}

/** The rows whose check got no answer, soft-deleted ones aside: what "Retry all" checks again. */
export function failedRows(rows: Iterable<Instrument>): Instrument[] {
	return Array.from(rows).filter((row) => !row.deleted && statusOf(row) === "failed");
}

/** The composition as it is saved: an instrument that cannot be held is left out. */
export function dropUnfixable(rows: Instrument[]): Instrument[] {
	return rows.filter((row) => statusOf(row) !== "unfixable");
}
